/**
 * perf-store.ts
 *
 * Lightweight in-memory store that accumulates per-component render data
 * collected via React Scan's programmatic `onRender` callback.
 *
 * Only active in development. Safe to import in any environment — all
 * operations become no-ops in production.
 */

const IS_DEV = process.env.NODE_ENV === "development";

// ─── Types ────────────────────────────────────────────────────────────────────

export type RenderEntry = {
  /** React component display name */
  name: string;
  /** Total number of times this component rendered */
  count: number;
  /** Cumulative render duration in milliseconds */
  totalMs: number;
  /** Shortest single render in milliseconds */
  minMs: number;
  /** Longest single render in milliseconds */
  maxMs: number;
  /** Timestamp of the most recent render */
  lastSeenAt: number;
  /** How many renders were flagged as "unnecessary" by React Scan */
  unnecessaryCount: number;
};

export type PerfSnapshot = {
  /** ISO timestamp when the snapshot was taken */
  capturedAt: string;
  /** Total renders recorded across all components */
  totalRenders: number;
  /** Components sorted by render count descending */
  components: RenderEntry[];
  /** Wall-clock ms since store was last reset */
  elapsedMs: number;
};

// ─── Store ────────────────────────────────────────────────────────────────────

const entries = new Map<string, RenderEntry>();
let storeStartedAt = Date.now();
let listeners: Array<() => void> = [];

function notify() {
  for (const fn of listeners) {
    fn();
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Record a single render event for a component.
 * Called from React Scan's `onRender` callback.
 */
export function recordRender(componentName: string, durationMs: number, unnecessary = false): void {
  if (!IS_DEV) return;

  const existing = entries.get(componentName);
  if (existing) {
    existing.count += 1;
    existing.totalMs += durationMs;
    existing.minMs = Math.min(existing.minMs, durationMs);
    existing.maxMs = Math.max(existing.maxMs, durationMs);
    existing.lastSeenAt = Date.now();
    if (unnecessary) existing.unnecessaryCount += 1;
  } else {
    entries.set(componentName, {
      count: 1,
      lastSeenAt: Date.now(),
      maxMs: durationMs,
      minMs: durationMs,
      name: componentName,
      totalMs: durationMs,
      unnecessaryCount: unnecessary ? 1 : 0,
    });
  }

  notify();
}

/**
 * Return a snapshot of current render data sorted by render count (desc).
 */
export function getSnapshot(): PerfSnapshot {
  const components = Array.from(entries.values()).sort((a, b) => b.count - a.count);

  const totalRenders = components.reduce((sum, e) => sum + e.count, 0);

  return {
    capturedAt: new Date().toISOString(),
    components,
    elapsedMs: Date.now() - storeStartedAt,
    totalRenders,
  };
}

/**
 * Return only components that exceed the given thresholds — useful for
 * surfacing genuine hot-spots rather than the full list.
 */
export function getHotspots(opts?: {
  minRenders?: number;
  minAvgMs?: number;
  unnecessaryOnly?: boolean;
}): RenderEntry[] {
  const { minRenders = 5, minAvgMs = 0, unnecessaryOnly = false } = opts ?? {};

  return Array.from(entries.values())
    .filter((e) => {
      if (e.count < minRenders) return false;
      const avgMs = e.count > 0 ? e.totalMs / e.count : 0;
      if (avgMs < minAvgMs) return false;
      if (unnecessaryOnly && e.unnecessaryCount === 0) return false;
      return true;
    })
    .sort((a, b) => b.count - a.count);
}

/**
 * Clear all accumulated data and reset the elapsed timer.
 */
export function resetStore(): void {
  if (!IS_DEV) return;
  entries.clear();
  storeStartedAt = Date.now();
  notify();
}

/**
 * Subscribe to store updates. Returns an unsubscribe function.
 */
export function subscribe(fn: () => void): () => void {
  listeners.push(fn);
  return () => {
    listeners = listeners.filter((l) => l !== fn);
  };
}

/**
 * Serialize the current snapshot to a pretty-printed JSON string,
 * ready for copy/paste or file download.
 */
export function exportJson(): string {
  return JSON.stringify(getSnapshot(), null, 2);
}

/**
 * Log a formatted summary table to the browser console.
 * Handy for quick in-context inspection without opening the panel.
 */
export function logSummary(): void {
  if (!IS_DEV) return;

  const snap = getSnapshot();
  const elapsed = (snap.elapsedMs / 1000).toFixed(1);

  console.group(
    `%c⚡ Perf snapshot — ${snap.totalRenders} renders in ${elapsed}s`,
    "color: #7c6ff7; font-weight: bold;",
  );

  const rows = snap.components.slice(0, 20).map((e) => ({
    "Avg ms": (e.totalMs / e.count).toFixed(2),
    Component: e.name,
    "Max ms": e.maxMs.toFixed(2),
    Renders: e.count,
    Unnecessary: e.unnecessaryCount,
  }));

  if (rows.length > 0) {
    console.table(rows);
  } else {
    console.log("No render data collected yet.");
  }

  console.groupEnd();
}
